import { Airframe, AirframeSlot, InstalledComponent } from "../../../../types/airframe";
import { Component } from "../../../../types/component";
import { getComponentTypeName } from "../../../../utils/componentTypeUtils";

const ASSEMBLY_TYPE_ORDER = ["Engine", "APU", "Landing Gear"] as const;

const ASSEMBLY_COLUMN_PREFIX: Record<(typeof ASSEMBLY_TYPE_ORDER)[number], string> = {
  Engine: "Engine",
  APU: "APU",
  "Landing Gear": "LG",
};

export type AssemblySlotColumn = {
  id: string;
  label: string;
  componentType: (typeof ASSEMBLY_TYPE_ORDER)[number];
  /** 1-based index within the assembly type (Engine1 → 1, Engine2 → 2). */
  slotIndex: number;
};

const normalizeId = (value: string | undefined | null): string =>
  value ? value.trim().toLowerCase() : "";

export function normalizeAssemblyComponentType(
  componentType: string | number | undefined
): (typeof ASSEMBLY_TYPE_ORDER)[number] | null {
  const mapped = getComponentTypeName(componentType);
  if (mapped === "Engine" || mapped === "APU" || mapped === "Landing Gear") {
    return mapped;
  }

  const compact = String(componentType ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  if (compact === "engine") return "Engine";
  if (compact === "apu") return "APU";
  if (compact === "landinggear") return "Landing Gear";

  return null;
}

export function buildAssemblySlotColumnLabel(
  componentType: (typeof ASSEMBLY_TYPE_ORDER)[number],
  slotIndex: number
): string {
  const prefix = ASSEMBLY_COLUMN_PREFIX[componentType];
  return `${prefix}${slotIndex}`;
}

export function buildAssemblySlotColumnKey(
  componentType: (typeof ASSEMBLY_TYPE_ORDER)[number],
  slotIndex: number
): string {
  return `${componentType}-${slotIndex}`;
}

export function getSlotInstalledComponentId(
  installed: InstalledComponent | null | undefined
): string {
  if (!installed) {
    return "";
  }

  const raw = installed as InstalledComponent & {
    ComponentId?: string;
  };

  return raw.componentId ?? raw.ComponentId ?? "";
}

/** Active assembly slots for a type, ordered by slot number then location. */
export function getActiveAssemblySlots(
  airframe: Airframe,
  componentType: (typeof ASSEMBLY_TYPE_ORDER)[number]
): AirframeSlot[] {
  return (airframe.slots ?? [])
    .filter((slot) => {
      if (!slot.isActive) {
        return false;
      }
      return normalizeAssemblyComponentType(slot.componentType) === componentType;
    })
    .sort((left, right) => {
      if (left.slotNumber !== right.slotNumber) {
        return left.slotNumber - right.slotNumber;
      }
      return (left.locationCode ?? "").localeCompare(right.locationCode ?? "", undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
}

function getMaxAssemblySlotCounts(airframes: Airframe[]): Record<
  (typeof ASSEMBLY_TYPE_ORDER)[number],
  number
> {
  const maxCounts: Record<(typeof ASSEMBLY_TYPE_ORDER)[number], number> = {
    Engine: 0,
    APU: 0,
    "Landing Gear": 0,
  };

  for (const airframe of airframes) {
    for (const componentType of ASSEMBLY_TYPE_ORDER) {
      const count = getActiveAssemblySlots(airframe, componentType).length;
      maxCounts[componentType] = Math.max(maxCounts[componentType], count);
    }
  }

  return maxCounts;
}

/**
 * Columns are driven by how many Engine/APU/LG positions exist on airframes
 * (max per type across the grid). Engine1 = first engine slot, etc.
 */
export function buildAssemblySlotColumns(airframes: Airframe[]): AssemblySlotColumn[] {
  const maxCounts = getMaxAssemblySlotCounts(airframes);
  const columns: AssemblySlotColumn[] = [];

  for (const componentType of ASSEMBLY_TYPE_ORDER) {
    for (let slotIndex = 1; slotIndex <= maxCounts[componentType]; slotIndex += 1) {
      columns.push({
        id: buildAssemblySlotColumnKey(componentType, slotIndex),
        label: buildAssemblySlotColumnLabel(componentType, slotIndex),
        componentType,
        slotIndex,
      });
    }
  }

  return columns;
}

export function findSlotForColumn(
  airframe: Airframe,
  column: AssemblySlotColumn
): AirframeSlot | undefined {
  const slots = getActiveAssemblySlots(airframe, column.componentType);
  return slots[column.slotIndex - 1];
}

export function getInstalledComponentForSlot(
  airframe: Airframe,
  slot: AirframeSlot,
  installedComponents: Component[],
  componentsCatalog: Component[] = []
): Component | undefined {
  const installedComponentId = normalizeId(getSlotInstalledComponentId(slot.installedComponent));

  const fromInstalledList = installedComponents.find(
    (component) =>
      normalizeId(component.slotId) === normalizeId(slot.id) ||
      (installedComponentId && normalizeId(component.id) === installedComponentId)
  );
  if (fromInstalledList) {
    return fromInstalledList;
  }

  if (!installedComponentId) {
    return undefined;
  }

  const fromCatalog = componentsCatalog.find(
    (component) => normalizeId(component.id) === installedComponentId
  );
  if (!fromCatalog) {
    return undefined;
  }

  return {
    ...fromCatalog,
    attachedToAirframe: airframe.tailNumber,
    attachmentLocation: slot.locationCode,
    airframeId: airframe.id,
    slotId: slot.id,
    slotNumber: slot.slotNumber,
  };
}

export function formatAssemblyCellValue(component: Component | undefined): string {
  if (!component) {
    return "—";
  }
  const model = component.model?.trim() ?? "";
  const serial = component.serialNumber?.trim() ?? "";
  if (model && serial) {
    return `${model} - ${serial}`;
  }
  return model || serial || "—";
}
